import React, { useState, useEffect } from 'react';
import { InputMask } from '@react-input/mask';
import CurrencyInput from 'react-currency-input-field';
import api from '../../../../config/axios';
import Swal from 'sweetalert2';
import { FaTimes, FaTrash, FaCheck } from 'react-icons/fa';
import DatePicker from '../../../../components/utils/datepicker';
import moment from 'moment';

const EditarLancamento = ({ lancamento, onClose, onLancamentoAtualizado }) => {
    const [categorias, setCategorias] = useState([]);
    const [centrosCusto, setCentrosCusto] = useState([]);
    const [contasBancarias, setContasBancarias] = useState([]);
    const [contaBancariaInfo, setContaBancariaInfo] = useState(null);
    const [showModalPagamento, setShowModalPagamento] = useState(false);
    const [dataPagamento, setDataPagamento] = useState(new Date());

    useEffect(() => {
        if (lancamento) {
            fetchCategorias();
            fetchCentrosCusto();
            fetchContasBancarias();
            if (lancamento.conta_bancaria_id) {
                fetchContaBancaria(lancamento.conta_bancaria_id);
            }
        }
    }, [lancamento]);

    const fetchCategorias = async () => {
        try {
            const response = await api.get('/categorias-financeiras?limit=200');
            setCategorias(response.data.records || []);
        } catch (error) {
            console.error('Erro ao carregar categorias');
        }
    };

    const fetchCentrosCusto = async () => {
        try {
            const response = await api.get('/centros-de-custo?limit=200');
            setCentrosCusto(response.data.records || []);
        } catch (error) {
            console.error('Erro ao carregar centros de custo');
        }
    };

    const fetchContasBancarias = async () => {
        try {
            const response = await api.get('/contas-bancarias/para-lancamentos');
            setContasBancarias(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar contas bancárias');
        }
    };

    const fetchContaBancaria = async (id) => {
        try {
            const response = await api.get(`/contas-bancarias/${id}`);
            setContaBancariaInfo(response.data);
        } catch (error) {
            console.error('Erro ao carregar conta bancária');
        }
    };

    const formatarData = (dataString) => {
        if (!dataString) return '';
        if (dataString.includes('-')) {
            const [ano, mes, dia] = dataString.split('-');
            return `${dia}/${mes}/${ano}`;
        }
        return dataString;
    };

    const formatarValor = (valor) => {
        if (!valor) return '';
        return parseFloat(valor).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatarFormaPagamento = (forma) => {
        const formas = {
            'dinheiro': 'Dinheiro',
            'cartao_credito': 'Cartão de Crédito',
            'cartao_debito': 'Cartão de Débito',
            'pix': 'PIX',
            'cheque': 'Cheque',
            'boleto': 'Boleto',
            'ted': 'TED',
            'doc': 'DOC'
        };
        return formas[forma] || forma || 'Nenhum';
    };

    const formatarStatus = (status) => {
        const statusFormatados = {
            'pendente': 'Pendente',
            'pago': 'Pago',
            'cancelado': 'Excluído',
            'estornado': 'Estornado'
        };
        return statusFormatados[status] || status || '-';
    };

    const formatarTipoLancamento = (tipo) => {
        const tipos = {
            'unico': 'Único',
            'parcelado': 'Parcelado',
            'fixo': 'Fixo',
            'transferencia': 'Transferência'
        };
        return tipos[tipo] || tipo || '-';
    };

    const formatarRepeticao = (quantidade, periodo) => {
        if (!quantidade || !periodo) return '-';
        const periodos = {
            'mensal': 'Mensal',
            'semanal': 'Semanal',
            'diario': 'Diário',
            'anual': 'Anual'
        };
        return `${quantidade}x ${periodos[periodo] || periodo}`;
    };

    const getContrastColor = (hexColor) => {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#ffffff';
    };

    const formatarOrigem = (origem) => {
        const origens = {
            'caixa': 'Caixa',
            'banco': 'Banco',
            'manual': 'Manual'
        };
        return origens[origem] || origem || '-';
    };

    const formatarTipoTransferencia = (tipo) => {
        const tipos = {
            'aplicacao': 'Aplicação',
            'aprovisionamento': 'Aprovisionamento',
            'pagamento_cartao': 'Pagamento de Cartão',
            'resgate': 'Resgate',
            'saque': 'Saque',
            'transferencia': 'Transferência'
        };
        return tipos[tipo] || tipo || '-';
    };

    const handleMarcarComoPago = () => {
        setDataPagamento(new Date());
        setShowModalPagamento(true);
    };

    const confirmarPagamento = async () => {
        try {
            const dataFormatada = moment(dataPagamento).format('YYYY-MM-DD');
            await api.post(`/lancamentos/${lancamento.id}/pagar`, { data_pagamento: dataFormatada });
            Swal.fire('Sucesso', 'Lançamento marcado como pago', 'success');
            setShowModalPagamento(false);
            onLancamentoAtualizado();
            onClose();
        } catch (error) {
            Swal.fire('Erro', error.response?.data?.message || 'Erro ao marcar como pago', 'error');
        }
    };

    const handleExcluirLancamento = () => {
        Swal.fire({
            title: 'Tem certeza?',
            text: 'Deseja excluir este lançamento?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, excluir',
            cancelButtonText: 'Não'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.post(`/lancamentos/${lancamento.id}/cancelar`);
                    Swal.fire('Sucesso', 'Lançamento excluído', 'success');
                    onLancamentoAtualizado();
                    onClose();
                } catch (error) {
                    Swal.fire('Erro', error.response?.data?.message || 'Erro ao excluir lançamento', 'error');
                }
            }
        });
    };

    if (!lancamento) return null;

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">Editar Lançamento</h4>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-lg-4 col-12">
                                <label className="form-label fw-bold">Tipo de Lançamento</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={lancamento.tipo_transferencia ? 'Transferência' : (lancamento.categoria?.tipo_contabil === 'receita' ? 'Receita' : 'Despesa')}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Valor</label>
                                <input
                                    type="text"
                                    value={`R$ ${formatarValor(Math.abs(lancamento.valor))}`}
                                    className="form-control"
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Valor Pago</label>
                                <input
                                    type="text"
                                    value={`R$ ${formatarValor(lancamento.valor_pago ? Math.abs(lancamento.valor_pago) : lancamento.valor_pago)}`}
                                    className="form-control"
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-lg-4 col-12">
                                <label className="form-label fw-bold">Status</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formatarStatus(lancamento.status)}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Data de Pagamento</label>
                                <InputMask
                                    mask="__/__/____"
                                    replacement={{ _: /\d/ }}
                                    className="form-control"
                                    value={formatarData(lancamento.data_pagamento)}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Data de Crédito</label>
                                <InputMask
                                    mask="__/__/____"
                                    replacement={{ _: /\d/ }}
                                    className="form-control"
                                    value={formatarData(lancamento.data_credito)}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-lg-4 col-12">
                                <label className="form-label fw-bold">Data de Lançamento</label>
                                <InputMask
                                    mask="__/__/____"
                                    replacement={{ _: /\d/ }}
                                    className="form-control"
                                    value={formatarData(lancamento.data_hora_lancamento?.split('T')[0])}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Data de Vencimento</label>
                                <InputMask
                                    mask="__/__/____"
                                    replacement={{ _: /\d/ }}
                                    className="form-control"
                                    value={formatarData(lancamento.data_vencimento)}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Data de Competência</label>
                                <InputMask
                                    mask="__/__/____"
                                    replacement={{ _: /\d/ }}
                                    className="form-control"
                                    value={formatarData(lancamento.data_competencia)}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-lg-4 col-12">
                                <label className="form-label fw-bold">Gestora</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={lancamento.gestora?.nome || ''}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Unidade</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={lancamento.unidade?.nome || ''}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Descrição</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={lancamento.descricao || ''}
                                    disabled
                                />
                            </div>
                        </div>

                        {lancamento.tipo_transferencia ? (
                            <div className="row mt-3">
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Tipo de Transferência</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formatarTipoTransferencia(lancamento.tipo_transferencia)}
                                        disabled
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Conta Origem</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={lancamento.contaOrigem?.nome_exibicao || ''}
                                        disabled
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Conta Destino</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={lancamento.contaDestino?.nome_exibicao || ''}
                                        disabled
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="row mt-3">
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Categoria</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={lancamento.categoria?.nome || ''}
                                        disabled
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Centro de Custo</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={lancamento.centroCusto?.nome || ''}
                                        disabled
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">
                                        {lancamento.categoria?.tipo_contabil === 'receita' ? 'Cliente' : 'Fornecedor'}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={lancamento.cliente?.nome || ''}
                                        disabled
                                    />
                                </div>
                            </div>
                        )}

                        <div className="row mt-3">
                            {!lancamento.tipo_transferencia && (
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Conta Bancária</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={contaBancariaInfo?.nome_exibicao || lancamento.contaBancaria?.nome_exibicao || ''}
                                        disabled
                                    />
                                </div>
                            )}
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Forma de Pagamento</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formatarFormaPagamento(lancamento.forma_pagamento)}
                                    disabled
                                />
                            </div>
                            {lancamento.bandeira && (
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3 mb-0 mb-lg-3">
                                    <label className="form-label fw-bold">Bandeira</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={lancamento.bandeira?.bandeira || ''}
                                        disabled
                                    />
                                </div>
                            )}
                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Número do Documento/Nota Fiscal</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={lancamento.numero_doc || ''}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-lg-3 col-12">
                                <label className="form-label fw-bold">Tipo de Lançamento</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formatarTipoLancamento(lancamento.tipo_lancamento)}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Parcela</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={lancamento.parcela_numero ? `${lancamento.parcela_numero}/${lancamento.parcela_total}` : '-'}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Repetição</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formatarRepeticao(lancamento.repeticao_quantidade, lancamento.repeticao_periodo)}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Origem</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formatarOrigem(lancamento.origem)}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-lg-6 col-12">
                                <label className="form-label fw-bold">Tags</label>
                                <div
                                    className="form-control d-flex flex-wrap gap-1 align-items-center"
                                    style={{
                                        minHeight: '38px',
                                        backgroundColor: '#e9ecef',
                                        opacity: 1
                                    }}
                                >
                                    {lancamento.tags && lancamento.tags.length > 0 ? (
                                        lancamento.tags.map(tag => (
                                            <span
                                                key={tag.id}
                                                className="badge"
                                                style={{
                                                    backgroundColor: tag.cor,
                                                    color: getContrastColor(tag.cor),
                                                    fontSize: '0.85rem',
                                                    padding: '6px 12px'
                                                }}
                                            >
                                                {tag.nome}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-muted">Nenhuma tag</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-12">
                                <label className="form-label fw-bold">Observações</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={lancamento.observacao || ''}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-danger" onClick={onClose}>
                            <FaTimes className="me-1" />
                            Fechar
                        </button>
                        {lancamento.status === 'pendente' && (
                            <>
                                <button type="button" className="btn btn-danger" onClick={handleExcluirLancamento}>
                                    <FaTrash className="me-1" />
                                    Excluir Lançamento
                                </button>
                                <button type="button" className="btn btn-success" onClick={handleMarcarComoPago}>
                                    <FaCheck className="me-1" />
                                    Marcar como Pago
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {showModalPagamento && (
                <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="fw-bold mt-2">Confirmar Pagamento</h4>
                                <button type="button" className="btn-close" onClick={() => setShowModalPagamento(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Data de Pagamento</label>
                                        <DatePicker
                                            selected={dataPagamento}
                                            onChange={(date) => setDataPagamento(date)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-danger" onClick={() => setShowModalPagamento(false)}>
                                    <FaTimes className="me-1" />
                                    Fechar
                                </button>
                                <button type="button" className="btn btn-success" onClick={confirmarPagamento}>
                                    <FaCheck className="me-1" />
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditarLancamento;